/**
 * useColumnPreferences.
 *
 * Reads and writes the user's column visibility preferences from
 * localStorage, keyed per table so different tables (Recurring,
 * Rules, Bank transactions, etc.) each get their own stored set.
 *
 * Required columns always stay visible, even if the stored set tries
 * to hide them (defensive against user tampering or stale stored
 * keys from before a column became required).
 *
 * Generic over the key type K so each table can use its own ColumnKey
 * union without casting:
 *
 *   const prefs = useColumnPreferences<ColumnKey>({
 *     storageKey: RECURRING_COLUMNS_STORAGE_KEY,
 *     defaults: DEFAULT_VISIBLE_COLUMNS,
 *     required: REQUIRED_COLUMNS,
 *   });
 */

import { useCallback, useEffect, useState } from "react";

export interface UseColumnPreferencesOptions<K extends string> {
  /** The localStorage key, e.g. "novala.recurring.visibleColumns". */
  storageKey: string;
  /** The default set of visible column keys (used on first load or reset). */
  defaults: ReadonlySet<K>;
  /** Keys the user cannot hide; always visible regardless of stored state. */
  required: ReadonlySet<K>;
}

export interface UseColumnPreferencesReturn<K extends string> {
  visible: ReadonlySet<K>;
  isVisible: (key: K) => boolean;
  toggle: (key: K) => void;
  setVisible: (key: K, visible: boolean) => void;
  reset: () => void;
}

/** Parse a stored JSON array of keys into a Set, defensively. */
function parseStored<K extends string>(
  raw: string | null,
  defaults: ReadonlySet<K>,
  required: ReadonlySet<K>,
): Set<K> {
  if (!raw) return new Set([...defaults, ...required]);
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set([...defaults, ...required]);
    const next = new Set<K>(
      parsed.filter((k): k is K => typeof k === "string"),
    );
    // Required columns are always visible, even if the stored set omits them.
    for (const r of required) next.add(r);
    return next;
  } catch {
    return new Set([...defaults, ...required]);
  }
}

export function useColumnPreferences<K extends string>(
  options: UseColumnPreferencesOptions<K>,
): UseColumnPreferencesReturn<K> {
  const { storageKey, defaults, required } = options;

  const [visible, setVisibleState] = useState<Set<K>>(() => {
    if (typeof window === "undefined") {
      return new Set([...defaults, ...required]);
    }
    return parseStored<K>(
      window.localStorage.getItem(storageKey),
      defaults,
      required,
    );
  });

  // Persist whenever the set changes.
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([...visible]));
    } catch {
      // localStorage can throw in private browsing mode etc.; ignore silently.
    }
  }, [storageKey, visible]);

  const isVisible = useCallback(
    (key: K) => visible.has(key),
    [visible],
  );

  const toggle = useCallback(
    (key: K) => {
      if (required.has(key)) return;
      setVisibleState((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [required],
  );

  const setVisible = useCallback(
    (key: K, value: boolean) => {
      if (required.has(key) && !value) return;
      setVisibleState((prev) => {
        const next = new Set(prev);
        if (value) next.add(key);
        else next.delete(key);
        return next;
      });
    },
    [required],
  );

  const reset = useCallback(() => {
    setVisibleState(new Set([...defaults, ...required]));
  }, [defaults, required]);

  return { visible, isVisible, toggle, setVisible, reset };
}